// Portfolio chart (multi-line, toggleable series, time-range filter) +
// Trades table — ported from
// design/design_handoff_nifty_bull/prototype/chart-trades.jsx and
// adapted for the real BacktestSummary shape:
//
//   curve[].date         (ISO yyyy-mm-dd, parsed to Date here)
//   curve[].deployedCash
//   curve[].equityValue
//   curve[].cashOnHold
//   curve[].total
//
//   trades[].symbol
//   trades[].action  ("BUY"|"SELL")
//   trades[].price
//   trades[].quantity
//   trades[].tradeTime (ISO datetime)
//   trades[].pnl  (number|null — only on closing SELLs)

function PortfolioChart({ curve, benchmarkCurve, benchmarkLabel }) {
    // Default to just the two headline series — Total Portfolio Value
    // and Benchmark. The other three (Equity Value, Cash Deployed,
    // Cash on Hold) are useful for digging into the portfolio
    // composition but visually noisy by default; user opts in via the
    // legend pills.
    const [series, setSeries] = React.useState({
        total: true, equityValue: false, deployedCash: false, cashOnHold: false, benchmark: true,
    });
    // Range is a (startFrac, endFrac, activePreset) triple. The
    // preset chips snap to a date-relative window from the latest
    // sample; dragging the brush handles directly clears the
    // preset and the user's window stays as set.
    const [startFrac, setStartFrac] = React.useState(0);
    const [endFrac, setEndFrac] = React.useState(1);
    const [activePreset, setActivePreset] = React.useState('ALL');
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [hover, setHover] = React.useState(null);
    // displayHover trails `hover` so the crosshair + tooltip can fade
    // out at their last position (instead of unmounting under the
    // user's pointer the instant they leave the plot). On null, it
    // holds the previous value for ~200ms — long enough to cover the
    // --motion-fast (150ms) opacity transition.
    const [displayHover, setDisplayHover] = React.useState(null);
    React.useEffect(() => {
        if (hover) {
            setDisplayHover(hover);
            return undefined;
        }
        const t = setTimeout(() => setDisplayHover(null), 200);
        return () => clearTimeout(t);
    }, [hover]);
    const svgRef = React.useRef(null);
    const [width, setWidth] = React.useState(900);
    const height = isFullscreen ? Math.min(560, window.innerHeight - 240) : 340;

    // Normalise curve entries so `date` is always a Date instance.
    // Also splice the benchmark's total at each date in by ISO date
    // string so a single hover shows both the strategy AND the
    // benchmark on the same vertical rule.
    const benchmarkByDate = React.useMemo(() => {
        const m = new Map();
        for (const p of (benchmarkCurve || [])) {
            const k = (p.date instanceof Date ? p.date.toISOString() : p.date).slice(0, 10);
            m.set(k, p.total);
        }
        return m;
    }, [benchmarkCurve]);

    const normalised = React.useMemo(() => (curve || []).map((p) => {
        const dateObj = p.date instanceof Date ? p.date : new Date(p.date);
        const key = dateObj.toISOString().slice(0, 10);
        return {
            ...p,
            date: dateObj,
            benchmark: benchmarkByDate.get(key),
        };
    }), [curve, benchmarkByDate]);

    React.useEffect(() => {
        const ro = new ResizeObserver((entries) => {
            // 280px floor matches the smallest viewport we care about
            // (phone-360 minus card padding). Lower than the prior
            // 400px so the chart no longer horizontally overflows on
            // sub-400px screens.
            for (const e of entries) setWidth(Math.max(280, e.contentRect.width));
        });
        if (svgRef.current?.parentElement) ro.observe(svgRef.current.parentElement);
        return () => ro.disconnect();
    }, []);

    // Narrow viewports: shrink the y-axis gutter so the plot has
    // room. The labels are already abbreviated (₹4.30L) so we don't
    // lose information by shaving 16px off the left.
    const isNarrow = width < 600;

    const ranges = ['1M', '3M', '6M', 'YTD', '1Y', 'ALL'];
    const filteredCurve = React.useMemo(() => {
        if (normalised.length === 0) return [];
        const n = normalised.length;
        const lo = Math.max(0, Math.min(n - 1, Math.floor(startFrac * (n - 1))));
        const hi = Math.max(0, Math.min(n - 1, Math.ceil(endFrac * (n - 1))));
        if (lo >= hi) return [normalised[lo]];
        return normalised.slice(lo, hi + 1);
    }, [normalised, startFrac, endFrac]);

    // Snap to a preset chip (1M / 3M / 6M / 1Y / YTD / ALL) — converts
    // the date-relative window into a (startFrac, endFrac) pair against
    // the full curve. Same logic the brush uses on drag.
    const selectPreset = React.useCallback((key) => {
        if (normalised.length === 0) {
            setActivePreset(key); return;
        }
        if (key === 'ALL') {
            setStartFrac(0); setEndFrac(1); setActivePreset('ALL');
            return;
        }
        const endDate = normalised[normalised.length - 1].date;
        let cutoff;
        if (key === '1M') cutoff = new Date(endDate - 30 * 864e5);
        else if (key === '3M') cutoff = new Date(endDate - 90 * 864e5);
        else if (key === '6M') cutoff = new Date(endDate - 180 * 864e5);
        else if (key === '1Y') cutoff = new Date(endDate - 365 * 864e5);
        else if (key === 'YTD') cutoff = new Date(endDate.getFullYear(), 0, 1);
        else { setActivePreset(key); return; }
        const startIdx = Math.max(0, normalised.findIndex((p) => p.date >= cutoff));
        const endIdx = normalised.length - 1;
        setStartFrac(startIdx / endIdx);
        setEndFrac(1);
        setActivePreset(key);
    }, [normalised]);

    // ESC key dismisses fullscreen. Effect-scoped so we only attach
    // the listener when actually fullscreen.
    React.useEffect(() => {
        if (!isFullscreen) return;
        const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
        document.addEventListener('keydown', onKey);
        // Lock background scroll while overlay is up.
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [isFullscreen]);

    const padding = isNarrow
        ? { top: 16, right: 16, bottom: 28, left: 48 }
        : { top: 20, right: 24, bottom: 32, left: 64 };
    const w = width - padding.left - padding.right;
    const h = height - padding.top - padding.bottom;

    // Include benchmark in the y-axis range when it's visible —
    // otherwise a 0-trade backtest where the strategy total stays
    // flat at initial cash causes the benchmark line to draw above
    // the top tick mark, outside the SVG plotting area.
    const visibleKeys = ['total', 'equityValue', 'deployedCash', 'cashOnHold'].filter((k) => series[k]);
    const allValues = filteredCurve.flatMap((p) => {
        const vs = visibleKeys.map((k) => p[k] || 0);
        if (series.benchmark && p.benchmark != null) vs.push(p.benchmark);
        return vs;
    });
    const maxY = Math.max(1, ...allValues);
    const minY = 0;

    const xAt = (i) => padding.left + (i / Math.max(1, filteredCurve.length - 1)) * w;
    const yAt = (v) => padding.top + h - (v - minY) / Math.max(1, (maxY - minY)) * h;

    // Always emit the SVG path data, regardless of `series[key]`
    // visibility — the legend toggle now controls opacity (so the
    // line can fade in/out smoothly), so the geometry has to stay
    // present even when the series is "off". Filtering on
    // `series[key]` here would collapse `d=""` and cancel the
    // opacity transition mid-flight.
    const pathFor = (key) => {
        if (filteredCurve.length === 0) return '';
        return filteredCurve.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p[key] || 0).toFixed(1)}`).join(' ');
    };

    const benchmarkColor = 'oklch(0.66 0.16 50)'; // warm orange — distinct from chart-1..4
    const seriesDefs = [
        {
            key: 'total', label: 'Total portfolio value',
            color: 'var(--chart-4)', strokeWidth: 2,
            hint: (<><strong>Total Portfolio Value</strong> — Cash on Hold + Equity Value.
                The headline number: what the account would be worth if you sold everything
                at the day's close.</>),
        },
        {
            key: 'benchmark', label: `Benchmark: ${benchmarkLabel || 'index'}`,
            color: benchmarkColor, strokeWidth: 1.75, dash: '6 4',
            hint: (<><strong>Benchmark</strong> — total value if the same lumpsum/SIP cash
                schedule had bought the {benchmarkLabel || 'chosen index'} instead of going
                through the strategy. Each tranche purchases fractional units of the index
                at that day's close and is held to the end. The line is the daily
                mark-to-market of those units. Lets you tell whether the strategy is
                actually beating the index it's drawing stocks from.</>),
        },
        {
            key: 'equityValue', label: 'Equity value',
            color: 'var(--chart-2)', strokeWidth: 1.75,
            hint: (<><strong>Equity Value</strong> — mark-to-market value of stocks you
                currently hold (sum of qty × current price for every open lot). Compare
                against Cash Deployed: if Equity Value &gt; Cash Deployed, your holdings
                are in unrealised profit; reverse means unrealised loss.</>),
        },
        {
            key: 'deployedCash', label: 'Cash deployed',
            color: 'var(--chart-1)', strokeWidth: 1.75,
            hint: (<><strong>Cash Deployed</strong> — the cost basis of stocks you currently
                hold (sum of qty × purchase price for every open lot). <em>Can rise above the
                initial capital</em> if the strategy rolls profits forward — sells holdings
                at a gain and re-invests the larger proceeds in new positions. It's
                the cumulative <em>cost</em> of what you're holding right now, not how
                much fresh cash you've ever put in.</>),
        },
        {
            key: 'cashOnHold', label: 'Cash on hold',
            color: 'var(--chart-3)', strokeWidth: 1.75, dash: '3 3',
            hint: (<><strong>Cash on Hold</strong> — un-deployed cash sitting in your account.
                Lumpsum starts at the full capital and shrinks as the strategy buys; SIP
                starts at zero and refills with one tranche on the 1st of each month.</>),
        },
    ];

    const yTicks = 4;
    const ticks = Array.from({ length: yTicks + 1 }, (_, i) => minY + (maxY - minY) * (i / yTicks));

    const xTickCount = Math.min(6, filteredCurve.length);
    const xTicks = filteredCurve.length > 0
        ? Array.from({ length: xTickCount }, (_, i) => Math.floor(i * (filteredCurve.length - 1) / Math.max(1, (xTickCount - 1))))
        : [];

    const handleMove = (e) => {
        if (!svgRef.current || filteredCurve.length === 0) return;
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const idx = Math.max(0, Math.min(filteredCurve.length - 1, Math.round((x - padding.left) / w * (filteredCurve.length - 1))));
        setHover({ idx, point: filteredCurve[idx] });
    };

    // Touch parity for the hover crosshair. Mobile users tap the
    // chart to read a value; drag along to track; the tooltip
    // persists until they tap outside the chart card. preventDefault
    // on the SVG keeps the page from scrolling while dragging.
    const handleTouch = (e) => {
        if (!svgRef.current || filteredCurve.length === 0) return;
        e.preventDefault();
        const touch = e.touches[0] || e.changedTouches[0];
        if (!touch) return;
        const rect = svgRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const idx = Math.max(0, Math.min(filteredCurve.length - 1, Math.round((x - padding.left) / w * (filteredCurve.length - 1))));
        setHover({ idx, point: filteredCurve[idx] });
    };

    // Tap outside the chart card dismisses the tooltip (mobile UX —
    // there's no mouseLeave to clear it). Listens at document level;
    // suppressed if the tap originated inside the chart's parent card.
    React.useEffect(() => {
        if (!hover) return;
        const onTapOutside = (e) => {
            if (svgRef.current && !svgRef.current.parentElement.contains(e.target)) {
                setHover(null);
            }
        };
        document.addEventListener('touchstart', onTapOutside, { passive: true });
        return () => document.removeEventListener('touchstart', onTapOutside);
    }, [hover]);

    const lastTotal = filteredCurve[filteredCurve.length - 1]?.total || 0;
    const firstTotal = filteredCurve[0]?.total || 0;
    const headlineDelta = firstTotal > 0 ? ((lastTotal / firstTotal - 1) * 100) : 0;

    const cardBody = (
        <div className="card chart-hero" style={{ padding: 0 }}>
            <div className="row between wrap gap-4" style={{ padding: '20px 24px 16px' }}>
                <div>
                    <div className="eyebrow mb-2">Portfolio timeline</div>
                    <div className="row-base gap-3">
                        <div className="tnum t-2xl fw-700" style={{ fontFamily: 'var(--font-sans)', letterSpacing: '-0.022em', fontFeatureSettings: "'tnum' on, 'zero' on" }}>
                            {UI.fmtINR(lastTotal, { compact: true })}
                        </div>
                        <span className={`pill tnum ${headlineDelta >= 0 ? 'up' : 'down'}`} style={{ fontSize: 'var(--text-sm)' }}>
                            {headlineDelta >= 0 ? '+' : ''}{headlineDelta.toFixed(2)}%
                        </span>
                    </div>
                </div>
                <div className="row gap-2 wrap">
                    <div className="seg">
                        {ranges.map((r) => (
                            <button key={r} className={r === activePreset ? 'active' : ''} onClick={() => selectPreset(r)}>{r}</button>
                        ))}
                    </div>
                    {activePreset == null && (
                        <button onClick={() => selectPreset('ALL')}
                            className="pill"
                            style={{
                                padding: '4px 12px', fontSize: 'var(--text-xs)',
                                background: 'var(--surface-2)', border: '1px solid var(--border)',
                                cursor: 'pointer', color: 'var(--text-2)',
                            }}>
                            × custom
                        </button>
                    )}
                    <button onClick={() => setIsFullscreen((v) => !v)}
                        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        style={{
                            width: 36, height: 36,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--surface-2)', border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer', color: 'var(--text-2)',
                        }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {isFullscreen ? (
                                <>
                                    <path d="M9 9 H4 V4" />
                                    <path d="M15 9 H20 V4" />
                                    <path d="M9 15 H4 V20" />
                                    <path d="M15 15 H20 V20" />
                                </>
                            ) : (
                                <>
                                    <path d="M4 9 V4 H9" />
                                    <path d="M20 9 V4 H15" />
                                    <path d="M4 15 V20 H9" />
                                    <path d="M20 15 V20 H15" />
                                </>
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            <div style={{ position: 'relative', padding: '0 8px' }}>
                <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible', touchAction: 'pan-y' }}
                    role="img"
                    aria-label={(() => {
                        if (filteredCurve.length === 0) {
                            return 'Portfolio chart, no data in selected range.';
                        }
                        const first = filteredCurve[0];
                        const last = filteredCurve[filteredCurve.length - 1];
                        const pct = first.total > 0 ? ((last.total / first.total) - 1) * 100 : 0;
                        const dir = pct >= 0 ? 'up' : 'down';
                        const visibleNames = seriesDefs.filter((d) => series[d.key]).map((d) => d.label).join(', ');
                        return `Portfolio chart from ${UI.fmtDate(first.date)} to ${UI.fmtDate(last.date)}, showing ${visibleNames || 'no series'}. Total ${dir} ${Math.abs(pct).toFixed(1)}% over the window.`;
                    })()}
                    onMouseMove={handleMove}
                    onMouseLeave={() => setHover(null)}
                    onTouchStart={handleTouch}
                    onTouchMove={handleTouch}
                >
                    {ticks.map((t, i) => (
                        <g key={i}>
                            <line x1={padding.left} x2={width - padding.right} y1={yAt(t)} y2={yAt(t)}
                                stroke="var(--border)" strokeDasharray={i === 0 ? '' : '2 3'} />
                            <text x={padding.left - 10} y={yAt(t) + 4} textAnchor="end"
                                style={{ fontSize: 10.5, fill: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                                {UI.fmtINR(t, { compact: true })}
                            </text>
                        </g>
                    ))}

                    {xTicks.map((i) => (
                        <text key={i} x={xAt(i)} y={height - padding.bottom + 18} textAnchor="middle"
                            style={{ fontSize: 10.5, fill: 'var(--text-3)', fontFamily: 'var(--font-sans)' }}>
                            {UI.fmtDate(filteredCurve[i].date, true)}
                        </text>
                    ))}

                    {/* Series paths stay mounted regardless of toggle
                        state — opacity fades over --motion-base when
                        the user toggles a legend pill, so the line
                        gracefully eases out instead of hard-cutting.
                        pointer-events guard so hidden series don't
                        intercept the hover hit-test.

                        On first mount, each path also runs the
                        `chart-draw` keyframe via pathLength=1 +
                        animated stroke-dashoffset, so the chart
                        composes itself in front of the user instead
                        of appearing fully-formed. Series lines that
                        are dashed by design (benchmark) skip the
                        draw-in (they'd fight the strokeDasharray
                        animation). */}
                    {seriesDefs.map((def, i) => (
                        <path key={def.key} d={pathFor(def.key)} fill="none"
                            stroke={def.color} strokeWidth={def.strokeWidth}
                            strokeLinecap="round" strokeLinejoin="round"
                            pathLength={def.dash ? undefined : 1}
                            strokeDasharray={def.dash}
                            className={def.dash ? undefined : 'chart-draw'}
                            style={{
                                opacity: series[def.key] ? 1 : 0,
                                pointerEvents: series[def.key] ? 'auto' : 'none',
                                transition: 'opacity var(--motion-base) var(--ease-out)',
                                animationDelay: def.dash ? undefined : `${i * 80}ms`,
                            }} />
                    ))}

                    {/* Leading-edge pulse — sits at the trailing tip of
                        the 'total' series and breathes 4↔8.5px on a
                        1.6s loop so the chart feels live even on a
                        finished backtest. Hidden when total is toggled
                        off. The CSS `chart-pulse` keyframe drives it. */}
                    {series.total && filteredCurve.length > 0 && (() => {
                        const last = filteredCurve[filteredCurve.length - 1];
                        const cx = xAt(filteredCurve.length - 1);
                        const cy = yAt(last.total || 0);
                        const color = seriesDefs.find((d) => d.key === 'total')?.color || 'var(--chart-4)';
                        return (
                            <g style={{ pointerEvents: 'none' }}>
                                <circle cx={cx} cy={cy} r="4" fill={color}
                                        opacity="0.18" className="chart-pulse" />
                                <circle cx={cx} cy={cy} r="3.5" fill={color}
                                        stroke="var(--surface)" strokeWidth="1.5" />
                            </g>
                        );
                    })()}

                    {/* Crosshair + per-series dots — kept mounted at
                        the last-known hover position and faded via
                        opacity so the entry / exit easing reads
                        smoothly. `displayHover` lags `hover` by 200ms
                        on null transitions so the fade-out can finish
                        before the elements would otherwise unmount. */}
                    <g style={{
                        opacity: hover ? 1 : 0,
                        transition: 'opacity var(--motion-fast) var(--ease-out)',
                        pointerEvents: 'none',
                    }}>
                        {displayHover && (
                            <>
                                <line
                                    x1={xAt(displayHover.idx)} x2={xAt(displayHover.idx)}
                                    y1={padding.top} y2={height - padding.bottom}
                                    stroke="var(--text-3)" strokeDasharray="3 3" opacity="0.5" />
                                {seriesDefs.map((def) => series[def.key] && (
                                    <circle key={def.key}
                                        cx={xAt(displayHover.idx)}
                                        cy={yAt(displayHover.point[def.key] || 0)}
                                        r="3.5" fill="white" stroke={def.color} strokeWidth="2" />
                                ))}
                            </>
                        )}
                    </g>
                </svg>

                {/* Hover tooltip card — same lag-and-fade pattern as
                    the crosshair so the panel doesn't pop on/off the
                    second the cursor enters / leaves the plot. */}
                <div style={{
                    position: 'absolute', pointerEvents: 'none',
                    left: displayHover
                        ? Math.min(width - 220, Math.max(8, xAt(displayHover.idx) + 12))
                        : 0,
                    top: 8,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '10px 12px',
                    boxShadow: 'var(--shadow-md)',
                    minWidth: 200,
                    opacity: hover ? 1 : 0,
                    transition: 'opacity var(--motion-fast) var(--ease-out), left var(--motion-fast) var(--ease-out)',
                }}>
                    {displayHover && (
                        <>
                            <div className="t-sm tx-3 mb-2">
                                {UI.fmtDate(displayHover.point.date)}
                            </div>
                            {seriesDefs.filter((d) => series[d.key]).map((d) => (
                                <div key={d.key} className="row between gap-4 mt-1">
                                    <div className="row gap-2 t-sm tx-2">
                                        <span style={{ width: 8, height: 8, borderRadius: 'var(--radius-xs)', background: d.color }} />
                                        {d.label}
                                    </div>
                                    <div className="mono tnum t-sm fw-600">
                                        {UI.fmtINR(displayHover.point[d.key], { compact: true })}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>

            {/* Range brush — full-curve thumbnail with two draggable
                handles defining the visible window. Drag a handle for
                a custom window; drag the body to pan. The chip row
                above auto-clears its preset on any drag. */}
            <div style={{ padding: '6px 24px 14px' }}>
                <RangeBrush totals={normalised.map((p) => p.total)}
                    startFrac={startFrac} endFrac={endFrac}
                    onChange={(s, e) => {
                        setStartFrac(s);
                        setEndFrac(e);
                        setActivePreset(null);
                    }} />
            </div>

            <div style={{ padding: '10px 24px 22px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {seriesDefs.map((def) => {
                    const on = series[def.key];
                    return (
                        <button key={def.key}
                            onClick={() => setSeries((s) => ({ ...s, [def.key]: !s[def.key] }))}
                            className="row gap-2 t-sm fw-500"
                            style={{
                                padding: '8px 14px',
                                background: on ? 'var(--surface)' : 'transparent',
                                border: `1px solid ${on ? 'var(--border-strong)' : 'var(--border)'}`,
                                borderRadius: 'var(--radius-pill)',
                                cursor: 'pointer',
                                color: on ? 'var(--text)' : 'var(--text-3)',
                                transition: 'all var(--motion-base) var(--ease-out)',
                                opacity: on ? 1 : 0.55,
                                boxShadow: on ? 'var(--shadow-selected-neutral)' : 'none',
                                transform: on ? 'translateY(-0.5px)' : 'none',
                            }}
                        >
                            <span style={{
                                width: 10, height: 10, borderRadius: 'var(--radius-xs)',
                                background: on ? def.color : 'transparent',
                                border: `1.5px solid ${def.color}`,
                            }} />
                            {def.label}
                            {def.hint && (
                                <span onClick={(e) => e.stopPropagation()}
                                    className="row" style={{ marginLeft: 2 }}>
                                    <UI.Hint width={300}>{def.hint}</UI.Hint>
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    if (!isFullscreen) return cardBody;

    // Fullscreen modal — full-viewport overlay with the same chart at
    // a larger plot size. ESC or backdrop click dismisses.
    return (
        <>
            {cardBody /* Render the inline placeholder so React keeps refs stable. Hidden under the overlay. */}
            <div onClick={(e) => { if (e.target === e.currentTarget) setIsFullscreen(false); }}
                style={{
                    position: 'fixed', inset: 0, zIndex: 100,
                    background: 'oklch(0.965 0.016 80 / 0.78)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 24,
                    animation: 'fade-in var(--motion-base) var(--ease-out) both',
                }}>
                <div style={{
                    width: '100%', maxWidth: 1280, maxHeight: '100%',
                    overflow: 'auto',
                    boxShadow: 'var(--shadow-lg)',
                }}>
                    {cardBody}
                </div>
            </div>
        </>
    );
}

// ---------- Range brush (web) ----------
//
// Thumbnail strip below the chart with two draggable handles defining
// the visible window. Mirrors the mobile RangeBrush in
// mobile/lib/features/dashboard/presentation/range_brush.dart so the
// UX is identical across web + Android + iOS (parity contract).
//
// Inputs are normalised fractions of the full curve (0.0 = first
// point, 1.0 = last). Outputs the same fractions on every drag.
function RangeBrush({ totals, startFrac, endFrac, onChange }) {
    const ref = React.useRef(null);
    const [width, setWidth] = React.useState(800);
    const [activeHandle, setActiveHandle] = React.useState(null); // 'start' | 'end' | 'body' | null
    const dragRef = React.useRef(null);
    const height = 64;
    const handleW = 14;
    // Inset the draggable axis from the track edges. At fraction 0
    // the handle sits `INSET` px from the left edge, not flush — so
    // both handles are fully visible on load (when the default range
    // is the full window) and always have room to grab.
    const INSET = 14;

    React.useEffect(() => {
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) setWidth(Math.max(160, e.contentRect.width));
        });
        if (ref.current) ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);

    const minWindow = 0.02;
    const handleHit = 22;
    const inner = Math.max(40, width - 2 * INSET);
    const fracToX = (f) => INSET + f * inner;
    const startX = fracToX(startFrac);
    const endX = fracToX(endFrac);

    // Thumbnail path of the totals series — drawn over the inner
    // track so it aligns with the handle range.
    const path = React.useMemo(() => {
        if (!totals || totals.length < 2) return '';
        let min = totals[0]; let max = totals[0];
        for (const v of totals) { if (v < min) min = v; if (v > max) max = v; }
        const span = (max - min) <= 0 ? 1 : (max - min);
        return totals.map((v, i) => {
            const x = INSET + (i / (totals.length - 1)) * inner;
            const y = height - ((v - min) / span) * (height - 8) - 4;
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
        }).join(' ');
    }, [totals, width]);

    const xToFrac = (clientX) => {
        if (!ref.current) return 0;
        const rect = ref.current.getBoundingClientRect();
        const x = clientX - rect.left - INSET;
        return Math.max(0, Math.min(1, x / inner));
    };

    const onPointerDown = (e) => {
        const rect = ref.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        let kind = null;
        if (Math.abs(x - startX) <= handleHit) kind = 'start';
        else if (Math.abs(x - endX) <= handleHit) kind = 'end';
        else if (x > startX && x < endX) kind = 'body';
        if (!kind) return;
        dragRef.current = {
            kind,
            offset: kind === 'body' ? xToFrac(e.clientX) - startFrac : 0,
        };
        setActiveHandle(kind);
        e.target.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
        if (!dragRef.current) return;
        const f = xToFrac(e.clientX);
        const d = dragRef.current;
        if (d.kind === 'start') {
            onChange(Math.min(f, endFrac - minWindow), endFrac);
        } else if (d.kind === 'end') {
            onChange(startFrac, Math.max(f, startFrac + minWindow));
        } else if (d.kind === 'body') {
            const win = endFrac - startFrac;
            const newStart = Math.max(0, Math.min(1 - win, f - d.offset));
            onChange(newStart, newStart + win);
        }
    };

    const onPointerUp = () => {
        dragRef.current = null;
        setActiveHandle(null);
    };

    // Per-handle visual block. Renders a 14×52-px rounded pill with a
    // soft accent shadow + an embedded vertical grip-dot column so it
    // clearly reads "drag me" without a tooltip. Grows by 6% while
    // active so the user feels the press.
    const Handle = ({ x, kind }) => {
        const active = activeHandle === kind;
        const scale = active ? 1.08 : 1.0;
        return (
            <div className="brush-handle" style={{
                position: 'absolute',
                left: x - handleW / 2,
                top: 6,
                width: handleW,
                height: height - 12,
                borderRadius: handleW / 2,
                background: 'var(--accent)',
                border: '1.5px solid rgba(255,255,255,0.85)',
                boxShadow: active
                    ? '0 6px 16px -2px oklch(0.40 0.18 268 / 0.5), 0 0 0 6px oklch(0.40 0.18 268 / 0.10)'
                    : '0 4px 10px -2px oklch(0.40 0.18 268 / 0.32)',
                transform: `scale(${scale})`,
                transformOrigin: 'center',
                transition: 'transform 180ms var(--ease-out), box-shadow 180ms var(--ease-out)',
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
            }}>
                {[0, 1, 2].map((i) => (
                    <span key={i} style={{
                        width: 2, height: 2,
                        background: 'white',
                        borderRadius: '50%',
                    }} />
                ))}
            </div>
        );
    };

    return (
        <div ref={ref}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
                position: 'relative', width: '100%', height,
                background: 'var(--surface-2)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                touchAction: 'none',
                cursor: activeHandle === 'body' ? 'grabbing' : 'pointer',
                userSelect: 'none',
                overflow: 'hidden',
            }}>
            <svg width={width} height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <path d={path} fill="none" stroke="var(--text-3)"
                    strokeWidth="1.4" strokeLinecap="round" opacity="0.45" />
                {/* Selected window overlay — soft fill + thin top/bottom rule */}
                <rect x={startX} y={0} width={Math.max(0, endX - startX)} height={height}
                    fill="var(--accent)" opacity="0.10" />
                <line x1={startX} y1={1} x2={endX} y2={1}
                    stroke="var(--accent)" strokeWidth="1.5" opacity="0.45" />
                <line x1={startX} y1={height - 1} x2={endX} y2={height - 1}
                    stroke="var(--accent)" strokeWidth="1.5" opacity="0.45" />
            </svg>
            <Handle x={startX} kind="start" />
            <Handle x={endX} kind="end" />
        </div>
    );
}

// ---------- Trades Table ----------
// Page-based pagination — user picks a page size from the size
// switcher (5 / 10 / 50) and navigates with Prev / Next. High-
// frequency strategies can produce thousands of trades; binding the
// rendered set to a single page is the cheapest way to keep the
// DOM size bounded without surprising the user with cumulative
// "Show more" growth. Default size matches what most reviewers
// scan in a single glance.
const TRADES_PAGE_SIZES = [5, 10, 50];
const TRADES_DEFAULT_PAGE_SIZE = 10;

function TradesTable({ trades, windowStart, windowEnd }) {
    const [query, setQuery] = React.useState('');
    const [side, setSide] = React.useState('ALL');
    // Pre-populate the date filter with the backtest window so the
    // user immediately sees what range the trades span instead of
    // empty inputs that imply "no filter applied".
    const [startDate, setStartDate] = React.useState(windowStart || '');
    const [endDate, setEndDate] = React.useState(windowEnd || '');
    const [sortBy, setSortBy] = React.useState({ key: 'date', dir: 'desc' });
    // Pagination: user-selectable page size (5 / 10 / 50) + page
    // index. When the filter / sort changes we snap back to page 0
    // so the user lands at the start of the new view rather than on
    // a stale page index that may now be out of bounds.
    const [pageSize, setPageSize] = React.useState(TRADES_DEFAULT_PAGE_SIZE);
    const [pageIndex, setPageIndex] = React.useState(0);

    // Trades from the API arrive with `tradeTime` (ISO string),
    // `action` ("BUY"|"SELL"), and (when known) companyName + isin +
    // upstoxUrl. Coerce to a uniform shape once.
    const normalised = React.useMemo(() => (trades || []).map((t, i) => ({
        id: t.id ?? `T${i}`,
        symbol: t.symbol,
        companyName: t.companyName,
        upstoxUrl: t.upstoxUrl,
        side: t.action,
        date: t.tradeTime instanceof Date ? t.tradeTime : new Date(t.tradeTime),
        qty: t.quantity,
        price: typeof t.price === 'number' ? t.price : Number(t.price),
        value: (typeof t.price === 'number' ? t.price : Number(t.price)) * t.quantity,
        pnl: t.pnl == null ? null : Number(t.pnl),
        note: t.strategyNote,
    })), [trades]);

    const filtered = React.useMemo(() => {
        return normalised.filter((t) => {
            if (side !== 'ALL' && t.side !== side) return false;
            if (query) {
                const q = query.toLowerCase();
                const hay = `${t.symbol || ''} ${t.companyName || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (startDate && t.date < new Date(startDate)) return false;
            if (endDate && t.date > new Date(endDate + 'T23:59:59')) return false;
            return true;
        }).sort((a, b) => {
            const k = sortBy.key;
            const dir = sortBy.dir === 'asc' ? 1 : -1;
            if (k === 'date') return (a.date - b.date) * dir;
            if (k === 'stock') return a.symbol.localeCompare(b.symbol) * dir;
            if (k === 'side') return a.side.localeCompare(b.side) * dir;
            if (k === 'value') return (a.value - b.value) * dir;
            if (k === 'pnl') return ((a.pnl ?? 0) - (b.pnl ?? 0)) * dir;
            return 0;
        });
    }, [normalised, query, side, startDate, endDate, sortBy]);

    const toggleSort = (key) => {
        setSortBy((prev) => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'desc' });
        setPageIndex(0);
    };

    // Snap back to page 0 whenever the filter changes — the existing
    // page index could be past the new last page, and even if it
    // weren't, the user's mental model on changing a filter is "show
    // me what's at the top of the new list", not "preserve scroll".
    React.useEffect(() => {
        setPageIndex(0);
    }, [query, side, startDate, endDate, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(pageIndex, totalPages - 1);
    const pageStart = safePage * pageSize;
    const pageEnd = Math.min(filtered.length, pageStart + pageSize);
    const pageRows = filtered.slice(pageStart, pageEnd);

    const Th = ({ col, label, align = 'left', hint }) => (
        <th style={{ textAlign: align, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(col)}>
            <span className="row gap-1" style={{ display: 'inline-flex' }}>
                {label}
                {sortBy.key === col && (
                    <span className="tx-2" style={{ fontSize: 9 }}>
                        {sortBy.dir === 'asc' ? '▲' : '▼'}
                    </span>
                )}
                {hint && (
                    <span onClick={(e) => e.stopPropagation()}>
                        <UI.Hint width={300}>{hint}</UI.Hint>
                    </span>
                )}
            </span>
        </th>
    );

    return (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="row between wrap gap-3" style={{ padding: '18px 20px 14px' }}>
                <div>
                    <div className="fw-600" style={{ fontSize: 'var(--text-lg)', letterSpacing: 'var(--tracking-heading)' }}>Trades executed</div>
                    <div className="t-sm tx-3 mt-1 tnum">
                        {filtered.length} of {normalised.length} trades
                    </div>
                </div>
                <div className="row wrap gap-2">
                    <div className="seg">
                        {['ALL', 'BUY', 'SELL'].map((s) => (
                            <button key={s} className={s === side ? 'active' : ''} onClick={() => setSide(s)}>
                                {s === 'ALL' ? 'All' : s === 'BUY' ? 'Buys' : 'Sells'}
                            </button>
                        ))}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <span className="tx-3 row" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                            <UI.Icon.Search />
                        </span>
                        <input className="input"
                            style={{ paddingLeft: 32, width: 180, padding: '8px 10px 8px 32px', fontSize: 'var(--text-base)' }}
                            placeholder="Search ticker…"
                            type="search"
                            autoComplete="off"
                            spellCheck={false}
                            aria-label="Search trades by ticker"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)} />
                    </div>
                    <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                        autoComplete="off"
                        aria-label="Filter trades from date"
                        style={{ width: 140, padding: '8px 10px', fontSize: 'var(--text-base)' }} />
                    <span className="t-sm tx-3">to</span>
                    <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                        autoComplete="off"
                        aria-label="Filter trades to date"
                        style={{ width: 140, padding: '8px 10px', fontSize: 'var(--text-base)' }} />
                    {(query || side !== 'ALL' || startDate || endDate) && (
                        <button className="btn sm ghost" onClick={() => { setQuery(''); setSide('ALL'); setStartDate(''); setEndDate(''); }}>
                            Clear
                        </button>
                    )}
                    {/* Mobile-only sort affordance — table headers are
                        hidden at <=768px so column-click sorting isn't
                        reachable. This <select> drives the same sortBy
                        state. */}
                    <select className="sort-mobile select"
                        style={{ width: 180 }}
                        value={`${sortBy.key}-${sortBy.dir}`}
                        onChange={(e) => {
                            const [key, dir] = e.target.value.split('-');
                            setSortBy({ key, dir });
                        }}>
                        <option value="date-desc">Sort: Date (newest)</option>
                        <option value="date-asc">Sort: Date (oldest)</option>
                        <option value="stock-asc">Sort: Stock (A→Z)</option>
                        <option value="stock-desc">Sort: Stock (Z→A)</option>
                        <option value="value-desc">Sort: Value (high→low)</option>
                        <option value="value-asc">Sort: Value (low→high)</option>
                        <option value="pnl-desc">Sort: P&L (high→low)</option>
                        <option value="pnl-asc">Sort: P&L (low→high)</option>
                    </select>
                </div>
            </div>

            <div style={{ maxHeight: 460, overflow: 'auto', borderTop: '1px solid var(--border)' }}>
                <table className="table">
                    <thead>
                        <tr>
                            <Th col="stock" label="Stock" />
                            <Th col="date" label="Date executed" />
                            <Th col="side" label="Order type" />
                            <Th col="value" label="Qty × Price" align="right" />
                            <Th col="value" label="Value" align="right" />
                            <Th col="pnl" label="P&amp;L" align="right"
                                hint={<>
                                    <strong>Realised P&amp;L</strong> — sells only.
                                    Each sell is FIFO-matched against the earliest open
                                    buys for the same symbol; matched lots contribute{' '}
                                    <code>(sellPrice − buyPrice) × qty</code>. A single
                                    sell can span multiple earlier buys at different
                                    prices, in which case the row's P&amp;L is the
                                    sum across those matched lots. Buys show
                                    {' '}<code>—</code> because they're opening trades
                                    only. Excludes brokerage / STT / GST — those costs
                                    are already debited from cash on hold by the paper
                                    adapter.
                                </>} />
                        </tr>
                    </thead>
                    <tbody className="stagger">
                        {filtered.length === 0 ? (
                            <tr><td colSpan="6" style={{ padding: 0 }}>
                                {/* Designed empty state — distinguishes
                                    "filter eliminated everything" (which
                                    needs a Clear-filters escape) from
                                    "strategy never traded" (which needs a
                                    nudge toward broader inputs). v1 audit
                                    flagged this conflation as a top-3
                                    confusion driver. */}
                                <TradesEmptyState
                                    hasFilter={!!(query || side !== 'ALL' || startDate || endDate)}
                                    tradesInRun={normalised.length}
                                    onClearFilters={() => { setQuery(''); setSide('ALL'); setStartDate(''); setEndDate(''); }}
                                />
                            </td></tr>
                        ) : pageRows.map((t) => (
                            <tr key={t.id} className="row-fade-in">
                                <td className="td-stock">
                                    <div className="row gap-3">
                                        <UI.CompanyLogo symbol={t.symbol} size={28} />
                                        <div className="min-w-0">
                                            {t.upstoxUrl ? (
                                                <a href={t.upstoxUrl} target="_blank" rel="noopener noreferrer"
                                                    className="t-base fw-600 tx trk-base"
                                                    translate="no"
                                                    style={{
                                                        textDecoration: 'none',
                                                        borderBottom: '1px dashed var(--text-4)',
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-text)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text)'}>
                                                    {t.companyName || t.symbol}
                                                </a>
                                            ) : (
                                                <div className="t-base fw-600 tx trk-base" translate="no">
                                                    {t.companyName || t.symbol}
                                                </div>
                                            )}
                                            <div className="mono t-xs tx-3 mt-1" translate="no">
                                                {t.symbol}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td data-label="Date executed">
                                    <div className="mono tnum t-base">
                                        {UI.fmtDate(t.date)}
                                        <span className="t-xs tx-3 tnum" style={{ marginLeft: 6 }}>
                                            {t.date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })} IST
                                        </span>
                                    </div>
                                </td>
                                <td data-label="Order type">
                                    <span className={t.side === 'BUY' ? 'side-buy' : 'side-sell'}>{t.side}</span>
                                </td>
                                <td data-label="Qty × Price" align="right" className="mono tnum t-base tx-2">
                                    {t.qty} × ₹{Number(t.price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td data-label="Value" align="right" className="mono tnum t-base fw-600">
                                    {UI.fmtINR(t.value)}
                                </td>
                                <td data-label="P&L" align="right" className="mono tnum t-base fw-600" style={{
                                    color: t.pnl == null ? 'var(--text-4)' : t.pnl >= 0 ? 'var(--up)' : 'var(--down)' }}>
                                    {t.pnl == null ? '—' : `${t.pnl >= 0 ? '+' : ''}${UI.fmtINR(t.pnl)}`}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Pagination footer — page-size switcher (5 / 10 / 50)
                + Prev / Next + page indicator. Hidden when filtered
                set fits in a single page so the footer doesn't add
                visual weight to short tables. */}
            {filtered.length > 0 && (
                <div className="row between wrap gap-3" style={{
                    padding: 'var(--space-4) var(--space-5)',
                    borderTop: '1px solid var(--border)',
                }}>
                    <div className="row gap-3">
                        <span className="t-sm tx-3">Rows per page</span>
                        <div className="seg" role="radiogroup" aria-label="Rows per page">
                            {TRADES_PAGE_SIZES.map((n) => (
                                <button key={n}
                                    role="radio"
                                    aria-checked={n === pageSize}
                                    className={n === pageSize ? 'active' : ''}
                                    onClick={() => setPageSize(n)}>
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="row gap-3">
                        <span className="t-sm tx-3 tnum">
                            {pageStart + 1}–{pageEnd} of {filtered.length}
                        </span>
                        <div className="row gap-2">
                            <button className="btn sm"
                                aria-label="Previous page"
                                disabled={safePage === 0}
                                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}>
                                ← Prev
                            </button>
                            <span className="t-sm tnum tx-2" style={{
                                minWidth: 92, textAlign: 'center',
                            }}>
                                Page {safePage + 1} of {totalPages}
                            </span>
                            <button className="btn sm"
                                aria-label="Next page"
                                disabled={safePage >= totalPages - 1}
                                onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}>
                                Next →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Empty-state component — used by the trades table when a filter
// returns nothing OR when the strategy emitted no trades for the
// selected window. Two distinct copy paths (filter vs no-data) so the
// user knows whether the next move is "loosen the filter" or "rerun
// with a different strategy / window".
function TradesEmptyState({ hasFilter, tradesInRun, onClearFilters }) {
    const filteredOut = hasFilter && tradesInRun > 0;
    const headline = filteredOut
        ? 'No trades match this filter'
        : 'No trades during this window';
    const subhead = filteredOut
        ? `Out of ${tradesInRun} trades in this run, none match your current ticker / side filter.`
        : 'The strategy never fired a buy or sell during the dates you picked. Try a longer window, a different universe, or a more sensitive strategy.';

    return (
        <div style={{
            padding: 'var(--space-9) var(--space-5)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 'var(--space-3)',
        }}>
            {/* Scene illustration — distinct artwork for each case.
                "Filter eliminated everything" gets the bull-with-
                magnifier; "strategy never traded" gets the bull at
                the closed-ledger desk. Two situations, two scenes. */}
            <img
                src={`./assets/scenes/${filteredOut ? 'filter' : 'no-trades'}.png`}
                width="240" height="240" alt=""
                loading="lazy" decoding="async"
                style={{
                    width: 240, height: 240, objectFit: 'contain',
                    userSelect: 'none', pointerEvents: 'none',
                }}
            />
            <div className="fw-600 tx" style={{ fontSize: 'var(--text-md)' }}>{headline}</div>
            <div className="t-sm tx-3" style={{
                maxWidth: 360, textAlign: 'center', lineHeight: 1.5,
            }}>{subhead}</div>
            {filteredOut && (
                <button className="btn sm" onClick={onClearFilters}
                    style={{ marginTop: 'var(--space-2)' }}>
                    Clear filters
                </button>
            )}
        </div>
    );
}

window.PortfolioChart = PortfolioChart;
window.TradesTable = TradesTable;
window.TradesEmptyState = TradesEmptyState;
